import { createContext, useContext } from 'react';
import type { MoneyTransferInitialData } from 'components/pages/Banks/Wallet/MoneyTransfer/types';
import type { color } from 'types/shared/worlds';

/**
 * Configuration options for the transfer function
 */
export interface TransferConfig {
  /** Theme color for the transfer modal */
  theme?: color;
  /** Callback function called on successful transfer */
  onSuccess?: () => void;
  /** Whether this is a standing order payment */
  isStandingOrderPayment?: boolean;
}

/**
 * Transfer function type definition
 */
type TransferFunction = (
  initialData?: MoneyTransferInitialData,
  config?: TransferConfig,
) => void;

/**
 * MoneyTransferContext type definition
 */
interface MoneyTransferContextType {
  transfer: TransferFunction;
}

export const MoneyTransferContext = createContext<MoneyTransferContextType>(
  {} as MoneyTransferContextType,
);

/**
 * Hook to access the transfer function from MoneyTransferProvider
 *
 * @returns The transfer function that opens the MoneyTransfer modal
 *
 * @example
 * ```tsx
 * const transfer = useTransfer();
 *
 * const handlePayment = () => {
 *   transfer(
 *     {
 *       sourceAccount: BankAccountTypes.mainAccount,
 *       receiptAccount: BankAccountTypes.external,
 *       amount: 100,
 *     },
 *     {
 *       theme: 'taxes',
 *       onSuccess: () => console.log('Payment successful'),
 *     }
 *   );
 * };
 * ```
 */
const useTransfer = (): TransferFunction => {
  const { transfer } = useContext(MoneyTransferContext);

  return transfer;
};

export default useTransfer;
